import { useCallback, useEffect, useState } from 'react';
import type { Ticket } from '@/entities/ticket';
import { apiErrorText, massIncidentsApi, ticketsApi, type MassIncidentSummary } from '@/shared/api';
import { isTicketFinal } from '@/shared/config';

const INCIDENT_TICKETS_LIMIT = 200;

export interface IncidentState {
  summary: MassIncidentSummary | null;
  tickets: Ticket[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

export function useIncident(incidentId: string | null): IncidentState {
  const [summary, setSummary] = useState<MassIncidentSummary | null>(null);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!incidentId) return;
    setLoading(true);
    setError(null);
    try {
      const [s, list] = await Promise.all([
        massIncidentsApi.get(incidentId),
        ticketsApi.list({
          massIncident: incidentId,
          includeClosed: true,
          sort: 'createdAt',
          dir: 'asc',
          limit: INCIDENT_TICKETS_LIMIT,
        }),
      ]);
      setSummary(s.data);
      const rows = Array.isArray(list.data) ? list.data : [];
      const isClosed = (t: Ticket) => isTicketFinal(t);
      setTickets([...rows.filter((t) => !isClosed(t)), ...rows.filter(isClosed)]);
    } catch (err) {
      setError(apiErrorText(err, 'Не удалось загрузить сбой'));
    } finally {
      setLoading(false);
    }
  }, [incidentId]);

  useEffect(() => {
    setSummary(null);
    setTickets([]);
    void reload();
  }, [reload]);

  return { summary, tickets, loading, error, reload };
}

export function isLiveTicket(t: Pick<Ticket, 'status' | 'isFinal'>): boolean {
  return !isTicketFinal(t);
}
